/**
 * Section-level readings for Blueprint BandScales — same axes as the question
 * preview sheet. Marker = mean; optional range = min–max across the set.
 */

import type { AssessmentBankQuestion } from "@/lib/mock/assessment-add-questions"
import { questionPreview } from "@/lib/question-preview"
import { questionReuse } from "@/lib/question-reuse"
import type { DiscriminationMethodId } from "@/lib/discrimination"
import { discriminationMethod } from "@/lib/discrimination"

/** Same clamp as `USAGE_SCALE_MAX` on the shared BandScale. */
const USAGE_AXIS_MAX = 6

export interface SectionScaleReading {
  /** Marker position on the shared axis (mean), or null when nothing to measure. */
  value: number | null
  /** Lowest value in the set, clamped to the axis. Null when no reading. */
  rangeLow: number | null
  /** Highest value in the set, clamped to the axis. Null when no reading. */
  rangeHigh: number | null
  reading: string
  aside?: string
}

function currentPerformance(row: AssessmentBankQuestion) {
  const { versions } = questionPreview(row)
  return versions.find((v) => v.current)?.performance ?? versions[0]?.performance
}

function emptyReading(aside?: string): SectionScaleReading {
  return {
    value: null,
    rangeLow: null,
    rangeHigh: null,
    reading: "No reading",
    aside,
  }
}

function clamp(n: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, n))
}

/** Mean times-used, with min–max spread across the section. */
export function sectionUsageReading(
  rows: AssessmentBankQuestion[]
): SectionScaleReading {
  if (rows.length === 0) {
    return emptyReading()
  }
  const values = rows.map(
    (row) =>
      questionReuse({
        id: row.id,
        timesUsed: row.timesUsed,
        usedInSameCourse: row.usedInSameCourse,
        neverUsed: row.neverUsed,
      }).timesUsed
  )
  const mean = values.reduce((a, b) => a + b, 0) / values.length
  const low = Math.min(...values)
  const high = Math.max(...values)
  const rounded = Math.round(mean * 10) / 10
  const rangeAside =
    low === high
      ? `${formatUsageCount(low)} · ${rows.length} in section`
      : `${formatUsageCount(low)}–${formatUsageCount(high)} · ${rows.length} in section`
  return {
    value: clamp(mean, 0, USAGE_AXIS_MAX),
    rangeLow: clamp(low, 0, USAGE_AXIS_MAX),
    rangeHigh: clamp(high, 0, USAGE_AXIS_MAX),
    reading:
      rounded === 0
        ? "Never used on average"
        : rounded === 1
          ? "Avg used once"
          : `Avg used ${rounded} times`,
    aside: rangeAside,
  }
}

function formatUsageCount(n: number): string {
  const rounded = Math.round(n * 10) / 10
  return Number.isInteger(rounded) ? `${rounded}` : `${rounded}`
}

/** Mean p-value across questions that have been sat, plus min–max. */
export function sectionDifficultyReading(
  rows: AssessmentBankQuestion[]
): SectionScaleReading {
  const sat = rows
    .map((row) => currentPerformance(row)?.pValue)
    .filter((v): v is number => v != null)
  if (sat.length === 0) {
    return emptyReading(rows.length > 0 ? "None sat yet" : undefined)
  }
  const mean = sat.reduce((a, b) => a + b, 0) / sat.length
  const low = Math.min(...sat)
  const high = Math.max(...sat)
  const pct = (v: number) => `${Math.round(v * 100)}%`
  const rangeAside =
    low === high
      ? `${pct(low)} · ${sat.length} of ${rows.length} sat`
      : `${pct(low)}–${pct(high)} · ${sat.length} of ${rows.length} sat`
  return {
    value: mean,
    rangeLow: low,
    rangeHigh: high,
    reading: `${Math.round(mean * 100)}% correct`,
    aside: rangeAside,
  }
}

/** Mean PBI or D across questions that have a score, plus min–max. */
export function sectionDiscriminationReading(
  rows: AssessmentBankQuestion[],
  methodId: DiscriminationMethodId
): SectionScaleReading {
  const method = discriminationMethod(methodId)
  const scored = rows
    .map((row) => {
      const perf = currentPerformance(row)
      if (!perf) return null
      return methodId === "pbi" ? perf.pbi : perf.dIndex
    })
    .filter((v): v is number => v != null)

  if (scored.length === 0) {
    return emptyReading(rows.length > 0 ? "No history yet" : undefined)
  }
  const mean = scored.reduce((a, b) => a + b, 0) / scored.length
  const low = Math.min(...scored)
  const high = Math.max(...scored)
  const fmt = (v: number) => v.toFixed(2)
  const rangeAside =
    low === high
      ? `${fmt(low)} · ${scored.length} of ${rows.length} scored`
      : `${fmt(low)}–${fmt(high)} · ${scored.length} of ${rows.length} scored`
  return {
    value: mean,
    rangeLow: low,
    rangeHigh: high,
    reading: `${method.abbreviation} ${mean.toFixed(2)}`,
    aside: rangeAside,
  }
}
